// ── Attendance tab — the weekly attendance rollup (Wave 3, REAL read) ───────
// Reuses attendance.forCohort wrapped in useHonestQuery -> CohortTabShell.

import { CollectionPagination, CollectionToolbar, DataTableV2 } from "@vjal/ui";
import { useMemo } from "react";

import { useHonestQuery } from "../../../components/honest-state";
import { getCoachingZ6Services } from "../../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../../hooks/coaching-query-keys";
import type { CoachingAttendanceLogDto } from "../../../services/coaching-z6-dtos";
import { CohortTabShell } from "./cohort-tab-shell";
import { mapAttendanceWeeks, useCohortAttendanceCollection } from "./cohort-attendance-collection";

export function CohortAttendanceTab({ cohortId }: { cohortId: string }) {
  const query = useHonestQuery<CoachingAttendanceLogDto>(
    coachingQueryKeys.z6.attendance(cohortId),
    () => getCoachingZ6Services().attendance.forCohort(cohortId),
    { isEmpty: (data) => (data.attendance?.weeks ?? []).length === 0 }
  );
  const allRows = useMemo(
    () => mapAttendanceWeeks(query.data?.attendance.weeks ?? []),
    [query.data?.attendance.weeks]
  );
  const collection = useCohortAttendanceCollection(allRows);

  return (
    <CohortTabShell
      title="Attendance"
      subtitle="Per-week attendance rollup. Awaiting-Zoom-data rows mean no attendance has been ingested yet."
      posture={query.posture}
      gate={query.gate}
      reference={query.reference}
      onRetry={query.retry}
      emptyTitle="No attendance recorded"
      emptyMessage="Weekly attendance will appear here once sessions are held and attendance is ingested."
    >
      <CollectionToolbar
        collection={collection}
        searchPlaceholder="Filter sessions"
        className="rounded-xl border border-line"
        stableChipNames
      />
      <div className="rounded-xl border border-line bg-white">
        <DataTableV2
          collection={collection}
          caption="Weekly attendance"
          emptyTitle="No attendance matches your search"
          emptyMessage="Clear or change the filters above to widen the view."
        />
        {collection.uiState === "ready" && <CollectionPagination collection={collection} className="px-4" />}
      </div>
    </CohortTabShell>
  );
}